/**
 * Per-issue-type ticket creation forms.
 *
 * Each form is tailored to a TicketCategory and collects fields that
 * mirror what the corresponding issue table displays:
 *
 *   - delivery   → DEL ID, driver, failure reason
 *   - customer   → customer (typeahead), order, issue type
 *   - payment    → invoice/order, customer, amount disputed, payment type
 *   - inventory  → SKU (typeahead), warehouse, issue type, qty
 *   - compliance → customer, product category, violation type
 *   - returns    → order, customer, item, return reason, refund preference
 *
 * The backend `tickets` table only stores generic columns (subject /
 * description / customer_id / order_id / priority). To preserve every
 * extra field without a schema change, each form composes a structured
 * `subject` line + a multi-line `description` body using `Key: value`
 * pairs. The detail modal then displays the description verbatim, so all
 * the structured info round-trips through GET /tickets/:id.
 */

import React, { useCallback, useState } from 'react';
import {
  CreateTicketInput,
  TicketCategory,
  TicketPriority,
} from '../../../services/ticketsApi';
import { customersApi } from '../../../services/customersApi';
import { listSkus } from '../../../services/skusApi';
import { SearchPicker, SearchPickerOption } from './SearchPicker';
import styles from './ticketTypeForms.module.css';

/** Common props every per-type form receives from the modal shell. */
export interface TypeFormProps {
  priority: TicketPriority;
  setPriority: (p: TicketPriority) => void;
  /** Called by the form when the user submits — the modal awaits this. */
  buildPayload: (build: () => CreateTicketInput | string) => void;
}

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/* ─── shared field helpers ──────────────────────────────────────────── */

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  required,
  children,
}) => (
  <label className={styles.field}>
    <span>{label}{required && <em className={styles.required}> *</em>}</span>
    {children}
  </label>
);

const PriorityRow: React.FC<{ value: TicketPriority; onChange: (p: TicketPriority) => void }> = ({
  value, onChange,
}) => (
  <Field label="Priority">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as TicketPriority)}
      className={styles.input}
    >
      {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
    </select>
  </Field>
);

/* ─── search fetchers ───────────────────────────────────────────────── */

async function searchCustomers(q: string): Promise<SearchPickerOption[]> {
  const res = await customersApi.list({ search: q, limit: 10 });
  return (res.data ?? []).map((c) => ({
    id: c.id,
    label: c.full_name || c.email,
    sublabel: [c.store_name, c.city, c.state].filter(Boolean).join(' · ') || c.email,
  }));
}

async function searchSkus(q: string): Promise<SearchPickerOption[]> {
  const res = await listSkus({ search: q, limit: 10 });
  return (res.data ?? []).map((s) => ({
    id: s.id,
    label: `${s.sku_code} — ${s.product_name}`,
    sublabel: s.category_name ? `${s.category_name} · stock ${s.total_stock}` : `stock ${s.total_stock}`,
  }));
}

/* ─── shared description-builder ────────────────────────────────────── */

function buildDescription(narrative: string, fields: Record<string, string | number | undefined | null>): string {
  const lines = Object.entries(fields)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(([k, v]) => `${k}: ${v}`);
  const meta = lines.length > 0 ? lines.join('\n') : '';
  if (narrative && meta) return `${narrative}\n\n— Details —\n${meta}`;
  return narrative || meta;
}

/* ─── per-type form components ──────────────────────────────────────── */

const ISSUE_TYPES_CUSTOMER = [
  'Wrong item received',
  'Missing order',
  'Account hold',
  'Billing question',
  'Service complaint',
  'Other',
];

export const CustomerForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [customer, setCustomer] = useState<SearchPickerOption | null>(null);
  const [orderId, setOrderId] = useState('');
  const [issueType, setIssueType] = useState(ISSUE_TYPES_CUSTOMER[0]);
  const [narrative, setNarrative] = useState('');

  const customerFetcher = useCallback(searchCustomers, []);

  React.useEffect(() => {
    buildPayload(() => {
      if (!customer) return 'Customer is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${issueType} — ${customer.label}${orderId ? ` (ORD-${orderId})` : ''}`;
      return {
        category: 'customer',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Issue Type': issueType,
          'Customer': customer.label,
          'Order': orderId ? `ORD-${orderId.padStart(6, '0')}` : undefined,
        }),
        customer_id: customer.id,
        order_id: orderId ? parseInt(orderId, 10) : undefined,
        source: 'call_center',
      };
    });
  }, [buildPayload, customer, orderId, issueType, narrative, priority]);

  return (
    <>
      <div className={styles.row2}>
        <Field label="Customer" required>
          <SearchPicker
            value={customer}
            onChange={setCustomer}
            placeholder="Search by name, store, city…"
            fetcher={customerFetcher}
            required
          />
        </Field>
        <Field label="Order # (optional)">
          <input
            type="number"
            min={1}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className={styles.input}
            placeholder="e.g. 12345"
          />
        </Field>
      </div>
      <div className={styles.row2}>
        <Field label="Issue Type" required>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)} className={styles.input}>
            {ISSUE_TYPES_CUSTOMER.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <PriorityRow value={priority} onChange={setPriority} />
      </div>
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="What did the customer report?"
        />
      </Field>
    </>
  );
};

/* ── Delivery ── */

const FAILURE_REASONS = [
  'Customer not available',
  'Wrong address',
  'Refused delivery',
  'Damaged in transit',
  'Vehicle issue',
  'Driver could not reach store',
  'Other',
];

export const DeliveryForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [orderId, setOrderId] = useState('');
  const [driver, setDriver] = useState('');
  const [reason, setReason] = useState(FAILURE_REASONS[0]);
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!orderId.trim()) return 'Delivery / Order # is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${reason} — DEL-${orderId.padStart(6, '0')}`;
      return {
        category: 'delivery',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Failure Reason': reason,
          'Delivery / Order': `DEL-${orderId.padStart(6, '0')}`,
          'Driver': driver || undefined,
        }),
        order_id: parseInt(orderId, 10),
        source: 'call_center',
      };
    });
  }, [buildPayload, orderId, driver, reason, narrative, priority]);

  return (
    <>
      <div className={styles.row2}>
        <Field label="Delivery / Order #" required>
          <input
            type="number" min={1}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className={styles.input}
            placeholder="e.g. 42180"
          />
        </Field>
        <Field label="Driver / Route">
          <input
            type="text"
            value={driver}
            onChange={(e) => setDriver(e.target.value)}
            className={styles.input}
            placeholder="Driver name or route code"
          />
        </Field>
      </div>
      <div className={styles.row2}>
        <Field label="Failure Reason" required>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className={styles.input}>
            {FAILURE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <PriorityRow value={priority} onChange={setPriority} />
      </div>
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="What happened on the delivery attempt?"
        />
      </Field>
    </>
  );
};

/* ── Payment ── */

const PAYMENT_ISSUE_TYPES = [
  'Disputed charge',
  'Payment mismatch',
  'Partial payment',
  'Refund request',
  'Bounced check',
  'Other',
];
const PAYMENT_METHODS = ['Cash', 'Check', 'ACH', 'Credit Card', 'Wire', 'Other'];

export const PaymentForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [customer, setCustomer] = useState<SearchPickerOption | null>(null);
  const [invoice, setInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(PAYMENT_METHODS[0]);
  const [issueType, setIssueType] = useState(PAYMENT_ISSUE_TYPES[0]);
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!customer) return 'Customer is required';
      if (!invoice.trim()) return 'Invoice / Order # is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${issueType} — INV-${invoice.padStart(6, '0')} ($${amount || '0'})`;
      return {
        category: 'payment',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Issue Type': issueType,
          'Customer': customer.label,
          'Invoice / Order': `INV-${invoice.padStart(6, '0')}`,
          'Amount Disputed': amount ? `$${amount}` : undefined,
          'Payment Method': method,
        }),
        customer_id: customer.id,
        order_id: invoice ? parseInt(invoice, 10) : undefined,
        source: 'call_center',
      };
    });
  }, [buildPayload, customer, invoice, amount, method, issueType, narrative, priority]);

  return (
    <>
      <Field label="Customer" required>
        <SearchPicker
          value={customer}
          onChange={setCustomer}
          placeholder="Search by name, store, city…"
          fetcher={searchCustomers}
          required
        />
      </Field>
      <div className={styles.row2}>
        <Field label="Invoice / Order #" required>
          <input
            type="number" min={1}
            value={invoice}
            onChange={(e) => setInvoice(e.target.value)}
            className={styles.input}
            placeholder="e.g. 8842"
          />
        </Field>
        <Field label="Amount Disputed">
          <input
            type="number" min={0} step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={styles.input}
            placeholder="0.00"
          />
        </Field>
      </div>
      <div className={styles.row2}>
        <Field label="Issue Type" required>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)} className={styles.input}>
            {PAYMENT_ISSUE_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Payment Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)} className={styles.input}>
            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
      </div>
      <PriorityRow value={priority} onChange={setPriority} />
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="Describe the payment dispute or mismatch."
        />
      </Field>
    </>
  );
};

/* ── Inventory ── */

const INVENTORY_ISSUE_TYPES = [
  'Damaged stock',
  'Missing batch',
  'Adjustment dispute',
  'Quality concern',
  'Expired product',
  'Other',
];

export const InventoryForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [sku, setSku] = useState<SearchPickerOption | null>(null);
  const [warehouse, setWarehouse] = useState('');
  const [issueType, setIssueType] = useState(INVENTORY_ISSUE_TYPES[0]);
  const [qty, setQty] = useState('');
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!sku) return 'SKU is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${issueType} — ${sku.label}`;
      return {
        category: 'inventory',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Issue Type': issueType,
          'SKU': sku.label,
          'Warehouse': warehouse || undefined,
          'Quantity Affected': qty || undefined,
        }),
        source: 'call_center',
      };
    });
  }, [buildPayload, sku, warehouse, issueType, qty, narrative, priority]);

  return (
    <>
      <Field label="SKU" required>
        <SearchPicker
          value={sku}
          onChange={setSku}
          placeholder="Search by SKU code or product name…"
          fetcher={searchSkus}
          required
        />
      </Field>
      <div className={styles.row2}>
        <Field label="Warehouse / Location">
          <input
            type="text"
            value={warehouse}
            onChange={(e) => setWarehouse(e.target.value)}
            className={styles.input}
            placeholder="e.g. WH-Main"
          />
        </Field>
        <Field label="Quantity Affected">
          <input
            type="number" min={0}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className={styles.input}
            placeholder="Units"
          />
        </Field>
      </div>
      <div className={styles.row2}>
        <Field label="Issue Type" required>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)} className={styles.input}>
            {INVENTORY_ISSUE_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <PriorityRow value={priority} onChange={setPriority} />
      </div>
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="What's wrong with the stock?"
        />
      </Field>
    </>
  );
};

/* ── Compliance ── */

const VIOLATION_TYPES = [
  'Expired license',
  'Restricted product sold',
  'Age verification failure',
  'Quantity limit exceeded',
  'Tax-stamp issue',
  'Documentation missing',
  'Other',
];
const PRODUCT_CATEGORIES = ['Tobacco', 'Hemp', 'CBD', 'Vape', 'Beverages', 'Other'];

export const ComplianceForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [customer, setCustomer] = useState<SearchPickerOption | null>(null);
  const [productCategory, setProductCategory] = useState(PRODUCT_CATEGORIES[0]);
  const [violation, setViolation] = useState(VIOLATION_TYPES[0]);
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!customer) return 'Customer is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${violation} — ${customer.label}`;
      return {
        category: 'compliance',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Violation Type': violation,
          'Customer': customer.label,
          'Product Category': productCategory,
        }),
        customer_id: customer.id,
        source: 'call_center',
      };
    });
  }, [buildPayload, customer, productCategory, violation, narrative, priority]);

  // Compliance defaults to high priority — most violations are time-sensitive.
  React.useEffect(() => {
    if (priority === 'medium') setPriority('high');
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Field label="Customer" required>
        <SearchPicker
          value={customer}
          onChange={setCustomer}
          placeholder="Search by name, store, city…"
          fetcher={searchCustomers}
          required
        />
      </Field>
      <div className={styles.row2}>
        <Field label="Product Category">
          <select
            value={productCategory}
            onChange={(e) => setProductCategory(e.target.value)}
            className={styles.input}
          >
            {PRODUCT_CATEGORIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Violation Type" required>
          <select value={violation} onChange={(e) => setViolation(e.target.value)} className={styles.input}>
            {VIOLATION_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
      </div>
      <PriorityRow value={priority} onChange={setPriority} />
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="Describe the violation, what was observed, and supporting context."
        />
      </Field>
    </>
  );
};

/* ── Returns ── */

const RETURN_REASONS = [
  'Defective product',
  'Wrong item shipped',
  'Damaged on arrival',
  'Customer changed mind',
  'Expired stock',
  'Quality issue',
  'Other',
];
const REFUND_PREFS = ['Refund', 'Replacement', 'Store credit'] as const;
type RefundPref = typeof REFUND_PREFS[number];

export const ReturnsForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [orderId, setOrderId] = useState('');
  const [customer, setCustomer] = useState<SearchPickerOption | null>(null);
  const [item, setItem] = useState<SearchPickerOption | null>(null);
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState(RETURN_REASONS[0]);
  const [refundPref, setRefundPref] = useState<RefundPref>('Refund');
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!orderId.trim()) return 'Order # is required';
      if (!customer) return 'Customer is required';
      if (!narrative.trim()) return 'Description is required';
      const subject = `${reason} — ORD-${orderId.padStart(6, '0')} (${refundPref})`;
      return {
        category: 'returns',
        priority,
        subject,
        description: buildDescription(narrative, {
          'Return Reason': reason,
          'Customer': customer.label,
          'Order': `ORD-${orderId.padStart(6, '0')}`,
          'Item': item?.label,
          'Quantity': qty,
          'Resolution Preference': refundPref,
        }),
        customer_id: customer.id,
        order_id: parseInt(orderId, 10),
        source: 'call_center',
      };
    });
  }, [buildPayload, orderId, customer, item, qty, reason, refundPref, narrative, priority]);

  return (
    <>
      <div className={styles.row2}>
        <Field label="Order #" required>
          <input
            type="number" min={1}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className={styles.input}
            placeholder="e.g. 12345"
          />
        </Field>
        <Field label="Customer" required>
          <SearchPicker
            value={customer}
            onChange={setCustomer}
            placeholder="Search…"
            fetcher={searchCustomers}
            required
          />
        </Field>
      </div>
      <Field label="Item being returned (optional)">
        <SearchPicker
          value={item}
          onChange={setItem}
          placeholder="Search SKU…"
          fetcher={searchSkus}
        />
      </Field>
      <div className={styles.row2}>
        <Field label="Return Reason" required>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className={styles.input}>
            {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Quantity">
          <input
            type="number" min={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className={styles.input}
          />
        </Field>
      </div>
      <div className={styles.row2}>
        <Field label="Resolution">
          <select
            value={refundPref}
            onChange={(e) => setRefundPref(e.target.value as RefundPref)}
            className={styles.input}
          >
            {REFUND_PREFS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <PriorityRow value={priority} onChange={setPriority} />
      </div>
      <Field label="Description" required>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
          placeholder="Condition of item, customer's account, anything the warehouse needs to know."
        />
      </Field>
    </>
  );
};

/* ── Fallback (other / unspecified) ── */

export const GenericForm: React.FC<TypeFormProps> = ({ priority, setPriority, buildPayload }) => {
  const [subject, setSubject] = useState('');
  const [narrative, setNarrative] = useState('');

  React.useEffect(() => {
    buildPayload(() => {
      if (!subject.trim()) return 'Subject is required';
      return {
        category: 'other',
        priority,
        subject: subject.trim(),
        description: narrative || undefined,
        source: 'call_center',
      };
    });
  }, [buildPayload, subject, narrative, priority]);

  return (
    <>
      <Field label="Subject" required>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={styles.input}
        />
      </Field>
      <PriorityRow value={priority} onChange={setPriority} />
      <Field label="Description">
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          className={styles.textarea}
        />
      </Field>
    </>
  );
};

export const FORM_FOR_CATEGORY: Record<TicketCategory, React.FC<TypeFormProps>> = {
  customer: CustomerForm,
  delivery: DeliveryForm,
  payment: PaymentForm,
  inventory: InventoryForm,
  compliance: ComplianceForm,
  returns: ReturnsForm,
  other: GenericForm,
};
